import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LegacyCard,
  Page,
  Badge,
  Box,
  TextField,
  Label,
  FormLayout,
  IndexTable,
  PageActions,
  Layout,
  Select,
  Text,
  InlineStack,
} from "@shopify/polaris";
import { ExportIcon, EmailFollowUpIcon, UndoIcon } from "@shopify/polaris-icons";
import EmailPdfDialog from "./support/EmailPdfDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import CostAdjustments from "./support/CostAdjustments";
import { useEditableCostAdjustments } from "./support/useEditableCostAdjustments";
import Payment from "./support/Payment";
import PaymentStatusBadge from "./support/PaymentStatusBadge";
import { paymentStatuses } from "./support/paymentStatuses";
import CustomDatePicker from "../common/CustomDatePicker/CustomDatePicker";
import {
  deletePurchaseOrder,
  updatePurchaseOrder,
  emailPdf,
  downloadFile,
  useUser,
  getPurchaseOrderHorseVariants,
  getExchangeRate,
} from "../../api_utils/requests";
import { getCostAndMargin, getTotal } from "./support/utils";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import {
  assertNumber,
  assertString,
  cloneNestedObjects,
  commaDelimiter,
  extractMessageFromError,
  formatMoney,
  roundToTwoDecimals,
  getCurrencySymbol,
  makeLineItemsMap,
  vendorPath,
} from "../../helper_functions/utils";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import {
  PurchaseOrderState,
  type IPurchaseOrder,
  type IPurchaseOrderLineItem,
  type PurchaseOrderPaymentAttributes,
  type PurchaseOrderLineItemsQueryParams,
  type PurchaseOrderUpdatePayload,
} from "../../api_utils/types";
import { PURCHASE_ORDER_HELP_PAGE_URL } from "../../constants";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";
import Comments from "./Comments";
import HorseLink from "../common/HorseLink";
import {
  defaultOrderedClosedPurchaseOrderSortValue,
  purchaseOrderTableSortOptions,
} from "./support/orderedClosedPurchaseOrderSortOptions";
import ImportDuties from "./closed/ImportDuties";
import TableControls from "../common/TableControls";
import { useVisibleColumns } from "../../hooks/useVisibleColumns";
import { dutyColumns } from "./closed/dutyColumns";

type ClosedPurchaseOrderLineItem = Omit<IPurchaseOrderLineItem, "price" | "entry_summary_line_number" | "duty_rate"> & {
  price: string;
  entry_summary_line_number: string;
  duty_rate: string;
};

type SortableClosedPurchaseOrderLineItem = ClosedPurchaseOrderLineItem & {
  sortIndex: number;
};

type PurchaseOrderLineItems = Record<number, SortableClosedPurchaseOrderLineItem>;

const editableFields = ["price", ...dutyColumns.map((column) => column.id)] as const;

const getPurchaseOrderLineItemsPayload = (
  purchaseOrderLineItems: PurchaseOrderLineItems,
  originalPurchaseOrderLineItems: PurchaseOrderLineItems,
): PurchaseOrderUpdatePayload["purchase_order_line_items_attributes"] =>
  Object.values(purchaseOrderLineItems).flatMap((purchaseOrderLineItem) => {
    const changedFields = editableFields.filter(
      (field) => purchaseOrderLineItem[field] !== originalPurchaseOrderLineItems[purchaseOrderLineItem.id][field],
    );

    return changedFields.length > 0
      ? [{
          id: purchaseOrderLineItem.id,
          ...Object.fromEntries(changedFields.map((field) => [field, purchaseOrderLineItem[field] || null])),
        }]
      : [];
  });

const Index = ({
  purchaseOrder: initialPurchaseOrder,
  refetch,
}: {
  purchaseOrder: IPurchaseOrder;
  refetch: () => void;
}): React.JSX.Element => {
  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    shopify.loading(loading);
  }, [loading]);

  const [payload, setPayload] = useState<PurchaseOrderUpdatePayload>({});
  const initialPurchaseOrderWithArrivalDate = {
    ...initialPurchaseOrder,
    arrival_date: initialPurchaseOrder.arrival_date ? new Date(initialPurchaseOrder.arrival_date) : new Date(),
  };
  const originalPurchaseOrder = useRef<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const handlePaymentChange = useCallback((attributes: PurchaseOrderPaymentAttributes): void => {
    setPurchaseOrder((previousPurchaseOrder) => ({ ...previousPurchaseOrder, ...attributes }));
    setPayload((previousPayload) => ({ ...previousPayload, ...attributes }));
    setIsDirty(true);
  }, []);
  const originalPolis = useRef<PurchaseOrderLineItems>(makeLineItemsMap<SortableClosedPurchaseOrderLineItem>([]));
  const [polis, setPolis] = useState<PurchaseOrderLineItems>(makeLineItemsMap<SortableClosedPurchaseOrderLineItem>([]));
  const [lineItemsLoaded, setLineItemsLoaded] = useState(false);
  const { visibleColumns, selectedColumns, setSelectedColumns } = useVisibleColumns("purchase-orders-closed", dutyColumns);
  const columnChoices = dutyColumns.map((column) => ({ value: column.id, label: column.title }));
  const [sortedValue, setSortedValue] = useState(defaultOrderedClosedPurchaseOrderSortValue);
  const handleSortChange = useCallback(([sort]: string[]): void => {
    setSortedValue(sort.replace(" ", "-"));
  }, []);
  const query = useMemo<PurchaseOrderLineItemsQueryParams>(
    () => ({
      by_sort: sortedValue,
    }),
    [sortedValue],
  );

  const fetchAndUpdateVariant = useCallback((): Promise<void> => {
    return getPurchaseOrderHorseVariants(purchaseOrder.id, query)
      .then((response) => {
        const purchaseOrderLineItems = response.map((purchaseOrderLineItem: IPurchaseOrderLineItem) => ({
          ...purchaseOrderLineItem,
          price: purchaseOrderLineItem.price ? new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
          }).format(assertNumber(purchaseOrderLineItem.price)) : undefined,
          entry_summary_line_number: assertString(purchaseOrderLineItem.entry_summary_line_number),
          description_of_goods: assertString(purchaseOrderLineItem.description_of_goods),
          classification_number: assertString(purchaseOrderLineItem.classification_number),
          duty_rate: assertString(purchaseOrderLineItem.duty_rate),
          sortIndex: undefined,
        }));
        const lineItemsMap = makeLineItemsMap<SortableClosedPurchaseOrderLineItem>(purchaseOrderLineItems);
        setPolis(lineItemsMap);
        originalPolis.current = cloneNestedObjects(lineItemsMap);
        setLineItemsLoaded(true);
      })
      .catch(() => {
        setPolis({} as PurchaseOrderLineItems);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [purchaseOrder.id, query]);

  useEffect(() => {
    void fetchAndUpdateVariant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const [emailPdfDialogActive, setEmailPdfDialogActive] = useState(false);

  const resetInitial = (newPurchaseOrder: IPurchaseOrder): void => {
    setPayload({});
    setPurchaseOrder(newPurchaseOrder);
    setIsDirty(false);
  };

  const [errorMessage, setErrorMessage] = useState("");

  const handleArrivalDateChange = (newArrivalDate: Date): void => {
    setPurchaseOrder({ ...purchaseOrder, arrival_date: newArrivalDate });
    setPayload({ ...payload, arrival_date: newArrivalDate });
    setIsDirty(true);
  };

  const handleLabelChange = (label: string): void => {
    setPurchaseOrder({ ...purchaseOrder, label });
    setPayload({ ...payload, label });
    setIsDirty(true);
  };

  const handleImportDutiesChange = useCallback(
    (attributes: Pick<IPurchaseOrder, "cargo_control_number" | "entry_summary_id" | "release_date">): void => {
      setPurchaseOrder((previousPurchaseOrder) => ({ ...previousPurchaseOrder, ...attributes }));
      setPayload((previousPayload) => ({ ...previousPayload, ...attributes }));
      setIsDirty(true);
    },
    [],
  );

  const {
    costAdjustments,
    setCostAdjustments,
    total: costAdjustmentsTotal,
    isLoading: isLoadingCostAdjustments,
    isSaving: isSavingCostAdjustments,
    save: saveCostAdjustments,
    discard: discardCostAdjustments,
  } = useEditableCostAdjustments(purchaseOrder.id);
  const setSummaryIsDirty = useCallback((summaryIsDirty: boolean): void => {
    setIsDirty(isDirty || summaryIsDirty);
  }, [isDirty]);

  const handleDiscardAction = useCallback((): void => {
    resetInitial(originalPurchaseOrder.current);
    setPolis(cloneNestedObjects(originalPolis.current));
    discardCostAdjustments();
  }, [originalPurchaseOrder, originalPolis, discardCostAdjustments]);

  const handleSaveClosed = async (): Promise<void> => {
    setLoading(true);
    const purchaseOrderLineItemsAttributes = getPurchaseOrderLineItemsPayload(polis, originalPolis.current);
    const mPayload = {
      purchase_order: {
        ...payload,
        ...(purchaseOrderLineItemsAttributes.length > 0 ? {
          purchase_order_line_items_attributes: purchaseOrderLineItemsAttributes,
        } : {}),
      },
    };
    const summarySavePromise = saveCostAdjustments();
    const updatePurchaseOrderPromise = updatePurchaseOrder(purchaseOrder.id, mPayload);

    return Promise.all([summarySavePromise, updatePurchaseOrderPromise])
      .then((): void => {
        resetInitial(purchaseOrder);
        originalPurchaseOrder.current = purchaseOrder;
        originalPolis.current = cloneNestedObjects(polis);
        shopify.toast.show("Save successful");
      })
      .catch((err: unknown): void => {
        Rollbar.error(err, mPayload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally((): void => {
        setLoading(false);
      });
  };

  const handleNoteChange = useCallback(
    (note: string) => {
      setPurchaseOrder({ ...purchaseOrder, note });
      setPayload({ ...payload, note });
      setIsDirty(true);
    },
    [purchaseOrder, payload],
  );

  const handleExportDymoCsv = async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `${purchaseOrderName} Dymo Labels by Horse.csv`;
    await downloadFile(`/purchase_orders/${purchaseOrder.id}/dymo_csv`, filename);
  };

  const handleEmailPdfModalClose = (): void => {
    setEmailPdfDialogActive(false);
  };

  const total = useMemo(() => getTotal(polis), [polis]);
  const purchaseOrderTotal = roundToTwoDecimals(total.totalCost + costAdjustmentsTotal);
  const { paymentStatus, paymentDueDateStatus } = paymentStatuses(
    purchaseOrder.amount_paid,
    purchaseOrder.payment_due_date,
    purchaseOrderTotal,
  );
  const paymentIsLoading = !lineItemsLoaded || isLoadingCostAdjustments;
  const sortedLineItems = useMemo(() => {
    return Object.values(polis).sort((leftLineItem, rightLineItem) => leftLineItem.sortIndex - rightLineItem.sortIndex);
  }, [polis]);

  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const purchaseOrderCurrency = purchaseOrder.currency || userCurrency;
  const currenciesMatch = purchaseOrderCurrency === userCurrency;
  const { data: exchangeRate } = useQuery({
    enabled: !currenciesMatch,
    queryFn: () => getExchangeRate(purchaseOrderCurrency, userCurrency),
    queryKey: ["exchangeRate", purchaseOrderCurrency, userCurrency],
  });
  const costExchangeRate = currenciesMatch ? 1 : exchangeRate?.rate;

  const handleLineItemPriceChange = useCallback((id: number) =>
    (price?: string): void => {
      const cleanPrice = price?.replace(/[^\d.-]/g, "") || "";
      setPolis((previousPolis: PurchaseOrderLineItems) => ({
        ...previousPolis,
        [id]: {
          ...previousPolis[id],
          price: cleanPrice,
        },
      }));
      setIsDirty(true);
    }, [setPolis, setIsDirty]);

  const handleLineItemDutyChange = useCallback(
    (field: typeof dutyColumns[number]["id"], id: number) => (value: string): void => {
      setPolis((previousPolis) => ({
        ...previousPolis,
        [id]: { ...previousPolis[id], [field]: value },
      }));
      setIsDirty(true);
    },
    [],
  );

  const handleRevertToOrdered = (): void => {
    setLoading(true);
    const mPayload: { purchase_order: PurchaseOrderUpdatePayload } = {
      purchase_order: {
        state: PurchaseOrderState.Ordered,
      },
    };
    updatePurchaseOrder(purchaseOrder.id, mPayload)
      .then(() => {
        shopify.toast.show("Purchase order reverted");
        refetch();
      })
      .catch((err: unknown): void => {
        Rollbar.error(err, mPayload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const navigate = useNavigate();

  const pageMarkup = purchaseOrder && (
    <Page
      actionGroups={[
        {
          title: "More actions",
          actions: [
            {
              content: "Export PDF",
              onAction: (): void => {
                const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
                const filename = `Horse Purchase Order ${purchaseOrderName}.pdf`;
                setLoading(true);
                downloadFile(`/purchase_orders/${purchaseOrder.id}.pdf${window.location.search}`, filename)
                  .then(() => {
                    setLoading(false);
                  })
                  .catch((err: unknown): void => {
                    Rollbar.error(err, filename);
                    setLoading(false);
                    setErrorMessage("Failed to generate PDF");
                  });
              },
              icon: ExportIcon,
            },
            {
              content: "Email PDF",
              onAction: (): void => {
                setEmailPdfDialogActive(true);
              },
              icon: EmailFollowUpIcon,
            },
            {
              content: "Export CSV",
              onAction: async (): Promise<void> => {
                const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
                const filename = `Horse Purchase Order ${purchaseOrderName}.csv`;
                await downloadFile(`/purchase_orders/${purchaseOrder.id}.csv${window.location.search}`, filename);
              },
              icon: ExportIcon,
            },
            {
              content: "Export Dymo CSV",
              onAction: handleExportDymoCsv,
              icon: ExportIcon,
            },
            {
              content: "Revert to ordered",
              onAction: handleRevertToOrdered,
              prefix: <UndoIcon />,
            },
          ],
        },
      ]}
      backAction={{
        content: "Purchase orders",
        // url: "/purchase_orders",
        onAction: async (): Promise<void> => {
          await shopify.saveBar.leaveConfirmation();

          navigate("/purchase_orders");
        },
      }}
      fullWidth={visibleColumns.length > 0}
      secondaryActions={[
        {
          content: "Receiving history",
          // url: `/inventory_level_histories?purchase_order_id=${purchaseOrder.id}`,
          plain: true,
          onAction: async (): Promise<void> => {
            await shopify.saveBar.leaveConfirmation();
  
            navigate(`/inventory_level_histories?purchase_order_id=${purchaseOrder.id}`);
          },
        },
      ]}
      title="Closed purchase order"
      titleMetadata={
        <InlineStack gap="200">
          <Badge>Closed</Badge>
          <PaymentStatusBadge status={paymentIsLoading ? null : paymentStatus} />
          <PaymentStatusBadge status={paymentIsLoading ? null : paymentDueDateStatus} />
        </InlineStack>
      }
    >
      <SaveBar
        handleDiscard={handleDiscardAction}
        handleSave={handleSaveClosed}
        isDirty={isDirty}
      />

      <EmailPdfDialog
        active={emailPdfDialogActive}
        onClose={handleEmailPdfModalClose}
        onSend={emailPdf}
        purchaseOrder={purchaseOrder}
        setErrorMessage={setErrorMessage}
      />
      <Layout>
        {errorMessage ? (
          <Layout.Section>
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section>
          <LegacyCard>
            {purchaseOrder.supplier ? (
              <LegacyCard.Section title="Supplier">
                <Label id="purchaseOrderSupplier">{purchaseOrder.supplier.name}</Label>
              </LegacyCard.Section>
            ) : (
              <LegacyCard.Section title="Vendor">
                <Label id="purchaseOrderVendor">
                  <HorseLink url={vendorPath(purchaseOrder.vendor)}>{purchaseOrder.vendor}</HorseLink>
                </Label>
              </LegacyCard.Section>
            )}
            <LegacyCard.Section title="Destination">
              <Label id="purchaseOrderDestinationLabel">
                <HorseLink url={`/horse_locations/${purchaseOrder.horse_location_id}`}>
                  {purchaseOrder.destination}
                </HorseLink>
              </Label>
            </LegacyCard.Section>
            <LegacyCard.Section>
              <FormLayout>
                <FormLayout.Group condensed>
                  <TextField
                    autoComplete="on"
                    label="Label"
                    onChange={handleLabelChange}
                    placeholder={purchaseOrder.id.toString()}
                    value={purchaseOrder.label || ""}
                  />
                  <CustomDatePicker
                    date={purchaseOrder.arrival_date}
                    label="Arrival date"
                    placeholder="2021-07-01"
                    setDate={handleArrivalDateChange}
                  />
                  {currenciesMatch ? <Select
                      disabled
                      id="currencies"
                      label="Currency"
                      options={[purchaseOrderCurrency]}
                      value={purchaseOrderCurrency}
                    /> : null}
                  {!currenciesMatch && (
                    <Select
                      disabled
                      helpText="Used for cost"
                      id="purchase-order-currency"
                      label="Purchase order currency"
                      options={[purchaseOrderCurrency]}
                      value={purchaseOrderCurrency}
                    />
                  )}
                  {!currenciesMatch && (
                    <Select
                      disabled
                      helpText="Used for price"
                      id="horse-currency"
                      label="Horse currency"
                      options={[userCurrency]}
                      value={userCurrency}
                    />
                  )}
                </FormLayout.Group>
              </FormLayout>
            </LegacyCard.Section>
            <LegacyCard.Section>
              <ImportDuties onChange={handleImportDutiesChange} purchaseOrder={purchaseOrder} />
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section variant="fullWidth">
          <LegacyCard>
            <TableControls
              choices={columnChoices}
              onChange={setSelectedColumns}
              onSort={handleSortChange}
              selected={selectedColumns}
              sortOptions={isDirty ? undefined : purchaseOrderTableSortOptions}
              sortSelected={[sortedValue.replace("-", " ")]}
            />
            <IndexTable
              headings={[
                { title: "Variant" },
                ...visibleColumns.map((column) => ({ title: column.title })),
                { title: "Quantity ordered" },
                { title: "Quantity received" },
                { title: "Cost" },
                { title: "Price" },
                { title: "Margin" },
                { title: "Line total" },
              ]}
              itemCount={sortedLineItems.length}
              selectable={false}
            >
              <IndexTable.Row id="closedPOTotalsRow" key={0} position={0} rowType="subheader">
                <IndexTable.Cell>Totals</IndexTable.Cell>
                {visibleColumns.map((column) => <IndexTable.Cell key={column.id} />)}
                <IndexTable.Cell>{commaDelimiter(total.quantity)}</IndexTable.Cell>
                <IndexTable.Cell>{commaDelimiter(total.received)}</IndexTable.Cell>
                <IndexTable.Cell>{formatMoney(total.cost, purchaseOrderCurrency)}</IndexTable.Cell>
                <IndexTable.Cell />
                <IndexTable.Cell />
                <IndexTable.Cell>{formatMoney(total.totalCost, purchaseOrderCurrency)}</IndexTable.Cell>
              </IndexTable.Row>
              {sortedLineItems.map((variant, index) => {
                const { convertedCost, margin } = getCostAndMargin(variant, costExchangeRate, !currenciesMatch);

                return (
                  <IndexTable.Row id={variant.id.toString()} key={variant.id} position={index + 1}>
                    <IndexTable.Cell>
                      <div className="medium_hv">
                        <HorseVariant key={variant.id} variant={variant} />
                      </div>
                    </IndexTable.Cell>
                    {visibleColumns.map((column) => (
                      <IndexTable.Cell key={column.id}>
                        <Box width={column.width}>
                          <TextField
                            autoComplete="off"
                            id={`${column.id}-${variant.id}`}
                            label={column.title}
                            labelHidden
                            name={column.id}
                            onChange={handleLineItemDutyChange(column.id, variant.id)}
                            step={column.step}
                            suffix={column.suffix}
                            type={column.type}
                            value={variant[column.id]}
                          />
                        </Box>
                      </IndexTable.Cell>
                    ))}
                    <IndexTable.Cell>{variant.quantity}</IndexTable.Cell>
                    <IndexTable.Cell>{variant.received}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {formatMoney(variant.cost, purchaseOrderCurrency)}
                      {convertedCost !== undefined ? (
                        <Text as="p" tone="subdued" variant="bodySm">
                          {formatMoney(convertedCost, userCurrency)}
                        </Text>
                      ) : null}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div className="w120">
                        <TextField
                          align="right"
                          autoComplete="off"
                          id={`price-${variant.id}`}
                          label="Price"
                          labelHidden
                          name="price"
                          onChange={handleLineItemPriceChange(variant.id)}
                          placeholder="0.00"
                          prefix={getCurrencySymbol(userCurrency)}
                          type="currency"
                          value={assertString(variant.price)}
                        />
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{margin}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {formatMoney(Number(variant.quantity || 0) * Number(variant.cost || 0), purchaseOrderCurrency)}
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <LegacyCard title="Note to supplier">
            <LegacyCard.Section>
              <TextField
                autoComplete="off"
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                error={purchaseOrder.note?.length >= 1000 ? "Note is too long" : ""}
                label="Note"
                maxLength={1000}
                multiline={2}
                onChange={handleNoteChange}
                value={purchaseOrder.note || ""}
              />
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <LegacyCard title="Summary">
            <LegacyCard.Section>
              <CostAdjustments
                costAdjustments={costAdjustments}
                currency={purchaseOrderCurrency}
                disabled={isLoadingCostAdjustments || isSavingCostAdjustments}
                lineTotal={Number(total.totalCost)}
                purchaseOrder={purchaseOrder}
                receivedTotal={roundToTwoDecimals(total.receivedCost + costAdjustmentsTotal)}
                setCostAdjustments={setCostAdjustments}
                setIsDirty={setSummaryIsDirty}
                total={purchaseOrderTotal}
              />
            </LegacyCard.Section>
          </LegacyCard>
          <Payment
            disabled={loading || paymentIsLoading}
            loading={paymentIsLoading}
            onChange={handlePaymentChange}
            purchaseOrder={purchaseOrder}
            total={purchaseOrderTotal}
          />
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: () => {
              setDeletionDialogueActive(true);
            },
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionDialogueActive}
        content="Are you sure you want to delete this purchase order? This action cannot be reversed."
        deleteItem={deletePurchaseOrder}
        gotoPath="/purchase_orders"
        itemId={purchaseOrder.id}
        setActive={setDeletionDialogueActive}
        title="Delete purchase"
        toastMessage="Purchase order deleted"
      />
      <div style={{ maxWidth: "640px", margin: "0 auto" }}>
        <Comments />
      </div>
      <Footer pageTitle="purchase orders" url={PURCHASE_ORDER_HELP_PAGE_URL} />
    </Page>
  );

  return pageMarkup;
};

export default Index;
